import { createContext, useContext, useState, useEffect } from 'react';
import { useData } from './DataContext';
import { useAuth } from './AuthContext';

const InvestigationContext = createContext();

export const useInvestigation = () => {
  const context = useContext(InvestigationContext);
  if (!context) {
    throw new Error('useInvestigation must be used within an InvestigationProvider');
  }
  return context;
};

export const InvestigationProvider = ({ children }) => {
  const { reports, companies } = useData();
  const { user } = useAuth();

  // ADDED DEMO INVESTIGATION HERE
  const [investigations, setInvestigations] = useState([
    {
      id: 'inv-demo-001',
      reportId: 'rep-demo-001', // Linked to the harassment report in DataContext
      investigationTitle: 'Utredning: Sak vedr. Per Hansen',
      investigator: 'Admin Eksempel',
      investigatorEmail: 'admin@eksempel.no',
      status: 'completed',
      severity: 'high',
      startDate: '2024-05-16',
      endDate: '2024-05-25',
      urgencyLevel: 'high',
      initialAssessment: {
        summary: 'Påstander om offentlig utskjelling og trakassering. Alvorlighetsgrad vurdert som høy grunnet maktubalanse og gjentakelse.',
        severity: 'high',
        potentialImpact: 'Dårlig arbeidsmiljø, sykemeldinger, omdømmetap.'
      },
      meetings: [
        { id: 1, type: 'reporter', participant: 'Anonym (via system)', date: '2024-05-17', summary: 'Bekreftet innholdet i varselet. Ønsker at adferden stopper.' },
        { id: 2, type: 'witness', participant: 'Vitne A', date: '2024-05-18', summary: 'Bekrefter hendelsesforløpet. Opplevde situasjonen som svært ubehagelig.' },
        { id: 3, type: 'reported', participant: 'Per Hansen', date: '2024-05-20', summary: 'Erkjenner å ha vært sint, men mener ordvalget er overdrevet.' }
      ],
      conclusion: {
        findings: 'Det er sannsynliggjort at hendelsen fant sted som beskrevet. Vitneutsagn støtter varslers fremstilling.',
        substantiated: 'yes',
        reasoning: 'Adferden bryter med bedriftens etiske retningslinjer pkt 4.2 om respektfull kommunikasjon.',
        recommendations: ['Skriftlig advarsel til Per Hansen', 'Lederkurs for Per Hansen', 'Oppfølgingssamtale om 3 mnd']
      },
      followUp: {
        actionPlan: 'Gjennomføre samtale med Per Hansen. Iverksette lederstøtte.',
        responsiblePerson: 'HR Sjef',
        reviewDate: '2024-08-20'
      }
    },
    // NEW: Active investigation for the HMS Incident to trigger the popup
    {
      id: 'inv-demo-002',
      reportId: 'rep-demo-002', // Matches "Klemskade" report
      reportTitle: 'Klemskade ved pakkebånd', // Added for popup title display
      reportType: 'hms_incident', // Added for popup type display
      investigationTitle: 'Utredning: Klemskade Pakkebånd C',
      investigator: 'HMS Ansvarlig',
      investigatorEmail: 'hms@demoprod.no',
      status: 'pending_start', // This status often triggers "Start Investigation" flows
      severity: 'high',
      startDate: new Date().toISOString().split('T')[0],
      dueDate: '2024-05-24', // Deadline for initial assessment
      urgencyLevel: 'high',
      initialAssessment: {
        summary: 'Ansatt fikk hånd i klem under vedlikehold. Alvorlig nestenulykke/personskade.',
        severity: 'high',
        potentialImpact: 'Personskade, sykefravær, produksjonsstans'
      }
    }
  ]);

  const [procedures, setProcedures] = useState([]);
  
  // NEW: Add notification to trigger the popup
  const [notifications, setNotifications] = useState([
    {
      id: 'notif-demo-002',
      type: 'investigation_required', // Triggers the popup
      reportId: 'rep-demo-002',
      title: 'Ny kritisk hendelse',
      message: 'Klemskade ved pakkebånd krever utredning.',
      severity: 'high',
      requiresAction: true,
      status: 'unread',
      createdAt: new Date().toISOString()
    }
  ]);

  // NEW: State for Access Codes and Responses
  // ADDED DEMO ACCESS CODE FOR PER HANSEN
  const [accessCodes, setAccessCodes] = useState([
    {
      id: 'ac-demo-001',
      reportId: 'rep-demo-001',
      code: 'PER-SAK-2024',
      role: 'reported_party',
      createdAt: '2024-05-16T10:00:00Z',
      expiresAt: '2025-12-31T23:59:59Z',
      isUsed: false,
      sanitizedDescription: 'Det er mottatt et varsel vedrørende din opptreden på avdelingsmøtet 14. mai. Det hevdes at du hevet stemmen og brukte ord som "ubrukelig" om en ansatt i andres påhør.',
      createdBy: 'Admin Eksempel'
    }
  ]);

  const [caseResponses, setCaseResponses] = useState([]);

  // --- EXISTING LOGIC PRESERVED ---
  useEffect(() => {
    const newReports = reports.filter(report =>
      report.status === 'new' &&
      !investigations.find(inv => inv.reportId === report.id)
    );
    // Logic to create auto investigation would go here
  }, [reports]);

  // --- NEW MODULE LOGIC: INNSYN OMVARSLET ---
  const generateAccessCode = (reportId, sanitizedDescription, validDays = 14) => {
    const code = `INS-${Math.floor(1000 + Math.random() * 9000)}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + validDays);

    const newAccessCode = {
      id: `AC-${Date.now()}`,
      reportId,
      code,
      role: 'reported_party',
      createdAt: new Date().toISOString(),
      expiresAt: expiresAt.toISOString(),
      isUsed: false,
      sanitizedDescription, // The edited text the reported party sees
      createdBy: user?.name || 'System'
    };

    setAccessCodes(prev => [newAccessCode, ...prev]);
    return newAccessCode;
  };

  const validateAccessCode = (inputCode) => {
    const found = accessCodes.find(ac => ac.code === inputCode);
    if (!found) return { valid: false, error: 'Ugyldig kode' };
    if (new Date() > new Date(found.expiresAt)) {
      return { valid: false, error: 'Koden er utløpt' };
    }
    return { valid: true, data: found };
  };

  const submitCaseResponse = (accessCodeId, reportId, content, wantsDialogue) => {
    const newResponse = {
      id: `RESP-${Date.now()}`,
      reportId,
      accessCodeId,
      content,
      wantsDialogue,
      submittedAt: new Date().toISOString(),
      status: 'new'
    };
    setCaseResponses(prev => [newResponse, ...prev]);

    // Mark code as used/logged
    setAccessCodes(prev => prev.map(ac => ac.id === accessCodeId ? { ...ac, isUsed: true } : ac));

    // Create notification for admin
    const notif = {
      id: `NOT-RESP-${Date.now()}`,
      type: 'response_received',
      title: 'Nytt tilsvar mottatt',
      message: 'Den omvarslede har sendt inn sitt tilsvar.',
      reportId: reportId,
      status: 'unread',
      createdAt: new Date().toISOString()
    };
    setNotifications(prev => [notif, ...prev]);

    return { success: true };
  };

  const getResponsesForReport = (reportId) => {
    return caseResponses.filter(r => r.reportId === reportId);
  };

  const getCodesForReport = (reportId) => {
    return accessCodes.filter(ac => ac.reportId === reportId);
  };

  // --- EXISTING FUNCTIONS MOCKED/PRESERVED FOR COMPATIBILITY ---
  const addInvestigation = (data) => setInvestigations(prev => [...prev, data]);
  const startInvestigation = (id) => {
    // Logic to start investigation (e.g. change status)
    setInvestigations(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'in_progress' } : inv));
  };
  const getInvestigationForReport = (reportId) => investigations.find(inv => inv.reportId === reportId);
  const getActiveNotifications = () => notifications;
  const getActiveProcedures = () => procedures;
  const markNotificationAsRead = (id) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));

  const value = {
    investigations,
    procedures,
    notifications,
    addInvestigation,
    startInvestigation,
    getInvestigationForReport,
    getActiveNotifications,
    getActiveProcedures,
    markNotificationAsRead,
    // New exports
    generateAccessCode,
    validateAccessCode,
    submitCaseResponse,
    getResponsesForReport,
    getCodesForReport
  };

  return (
    <InvestigationContext.Provider value={value}>
      {children}
    </InvestigationContext.Provider>
  );
};